/* -----------------Helpers & Hooks--------------- */
import { useLocalSearchParams } from "expo-router";

import { cacheKeys } from "@constants/cache-keys";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useReviewCache } from "@features/feedback/hooks/useReviewCache";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { isWeb } from "@helpers/platform";
import useAuthUser from "@hooks/useAuthUser";
import useCacheKey from "@hooks/useCacheKey";
import { useGlobalStore } from "@hooks/useGlobalStore";
/* -----------------Child components--------------- */
import { useMutationState, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { getReport } from "../api";

export const useReport = () => {
	const params = useLocalSearchParams<{
		id: string;
	}>();

	const trackAppReviewCriteria = useReviewCache({
		isRoutedFromSession: true,
		shouldInvalidate: true,
	}).trackAppReviewCriteria;
	const id = params?.id;

	const { baseCacheKey } = useCacheKey();
	const updateAnswerEventInProgress = useSessionStore(
		(state) => state.updateAnswerEventInProgress,
	);

	const isPremiumUser = useGlobalStore((state) => state.isPremiumUser);

	const isSummativeTestFromSessionStore = useSessionStore(
		(state) => state.isSummativeTest,
	);
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();

	const sessionId = useSessionStore((state) => state.id);

	const isAnswerEventInProgress = useSessionStore(
		(state) => state.isAnswerEventInProgress,
	);

	const { userPublicMetadataId: userId } = useAuthUser();

	const isEndingSessionInProgress = useSessionStore(
		(state) => state.isEndingSessionInProgress,
	);

	const pendingMutations = useMutationState({
		filters: {
			mutationKey: [baseCacheKey, cacheKeys.answerEvent],
			status: "pending",
		},
	});

	useEffect(() => {
		if (pendingMutations.length === 0) {
			updateAnswerEventInProgress(false);
		}
	}, [pendingMutations.length, updateAnswerEventInProgress]);

	const { data: reportDetails } = useQuery({
		queryKey: ["report", id, userId],
		queryFn: async () => {
			// if (isMocked) {
			// 	return mockedReportResponse;
			// }
			const reportDetails = await getReport({
				sessionId: Number(id),
				userId: userId!,
			});
			const isSummativeTest = reportDetails
				? reportDetails?.reportSessionType === "SUMMATIVE" &&
					studyPackCollectionId !== 38
				: isSummativeTestFromSessionStore;
			if (
				reportDetails &&
				!isSummativeTest &&
				sessionId &&
				reportDetails.score &&
				isPremiumUser &&
				!isWeb
			) {
				const score =
					typeof reportDetails.score === "string"
						? Number.parseFloat(reportDetails.score)
						: reportDetails.score;
				trackAppReviewCriteria(score);
			}
			return reportDetails;
		},
		enabled:
			!(isAnswerEventInProgress || isEndingSessionInProgress) &&
			Boolean(id) &&
			Boolean(userId),
	});

	return {
		reportDetails,
	};
};
